import React, { useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import ConfirmationModal from 'legacy/app/components/modal/ConfirmationModal';
import {
	TFinanceChargeAccount,
	TOnCreateArgs,
} from 'legacy/pages/accounts-payable/credit-card/reconcile/FinanceCharge/FinanceChargeTypes';

import AccountsDropdown, {
	EAccountType,
} from 'legacy/pages/accounts-payable/credit-card/reconcile/AccountsDropdown';
import { IJournalEntry } from 'api/resources/financeCharges';
import {
	FINANCE_CHARGE_MAX_VALUE,
	FINANCE_CHARGE_MAX_VALUE_ERROR,
} from '../constants/constants';

type TCharge = {
	account: TFinanceChargeAccount | null;
	damt: number;
};

type TFinanceChargeCreateProps = {
	showModal: boolean;
	onToggleModal: () => void;
	account: TFinanceChargeAccount;
	charge: TCharge | null;
	journalEntries: IJournalEntry[];
	onCreate: (values: TOnCreateArgs) => void;
};

const defaultValues: TCharge = {
	account: null,
	damt: 0,
};

export const FinanceChargeCreate = ({
	showModal,
	onToggleModal,
	account,
	charge,
	journalEntries,
	onCreate,
}: TFinanceChargeCreateProps) => {
	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<TCharge>({
		defaultValues,
	});

	const financeChargeTotal =
		journalEntries.reduce(
			(accumulator, currentValue) => accumulator + (currentValue?.damt ?? 0),
			0
		) ?? 0;

	const omitAccounts = [
		...(charge?.account ? [] : journalEntries.map((entry) => entry.account)),
		account.value,
	];

	useEffect(() => {
		if (showModal) {
			reset();
		}
	}, [showModal, reset]);

	const onSubmit = (data: TCharge) => {
		onCreate(data);
		onToggleModal();
	};

	return (
		<ConfirmationModal
			title="Service/Finance Charge Distribution"
			show={showModal}
			toggleModal={onToggleModal}
			footer={false}
			labelCancel="Cancel"
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="tw-grid tw-grid-flow-row tw-gap-2 tw-px-4 tw-pb-4">
					<div className="tw-grid tw-grid-flow-col tw-grid-cols-[1fr_6fr] tw-items-center">
						<Form.Label>Account:</Form.Label>
						<Controller
							name="account"
							control={control}
							render={({ field }) => (
								<AccountsDropdown
									type={EAccountType.ALL}
									omitAccounts={omitAccounts}
									isDisabled={typeof charge?.account === 'string' ?? false}
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...field}
								/>
							)}
						/>
					</div>
					<div className="tw-grid tw-grid-flow-col tw-grid-cols-[1fr_6fr] tw-items-baseline">
						<Form.Label>Amount:</Form.Label>
						<div>
							<Form.Control
								type="number"
								step={0.01}
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...register('damt', {
									required: true,
									valueAsNumber: true,
									validate: (value) =>
										value + financeChargeTotal <= FINANCE_CHARGE_MAX_VALUE ||
										FINANCE_CHARGE_MAX_VALUE_ERROR,
								})}
							/>
							{errors.damt && (
								<span className="tw-text-red-500 tw-text-xs tw-mt-1">
									{errors.damt.message}
								</span>
							)}
						</div>
					</div>
				</div>
				<Modal.Footer className="bg-ivory tw-grid tw-grid-flow-col tw-gap-2">
					<Button variant="primary" type="submit">
						OK
					</Button>
					<Button
						variant="trans-light border-secondary-ash"
						onClick={onToggleModal}
					>
						Cancel
					</Button>
				</Modal.Footer>
			</form>
		</ConfirmationModal>
	);
};

FinanceChargeCreate.displayName = 'FinanceChargeCreate';
